import { Slot } from "@radix-ui/react-slot";
import { useMemo, type HTMLAttributes, type Ref } from "react";

import { cn } from "../../lib/utils";
import { CardBody } from "./CardBody";
import { CardContext } from "./CardContext";
import { CardDescription } from "./CardDescription";
import { CardFooter } from "./CardFooter";
import { CardHeader } from "./CardHeader";
import { CardSection } from "./CardSection";
import { CardTitle } from "./CardTitle";
import { cardVariants, type CardVariant } from "./styles";

type CardPaddingProps =
  | {
      /**
       * Where the vertical spacing lives. `parts`: each region pads itself, so a `className`
       * override on a part changes only that part. `root`: the card pads its top/bottom edges and
       * gaps its regions, and the parts pad only horizontally. Clean Header/Body/Footer
       * compositions look identical either way.
       * @default "parts"
       */
      padding?: "parts";
      /**
       * Draws a divider between each region and between `Card.Section`s. Requires
       * `padding="parts"`, since a divider needs padding on both sides of it.
       * @default false
       */
      divided?: boolean;
    }
  | {
      padding: "root";
      divided?: false;
    };

export type CardProps = HTMLAttributes<HTMLDivElement> &
  CardPaddingProps & {
    ref?: Ref<HTMLDivElement>;
    /**
     * `default` is a bordered surface with no shadow. `floating` is borderless, more rounded,
     * elevated with a large shadow, and padded more generously.
     * @default "default"
     */
    variant?: CardVariant;
    /**
     * Merge the card's props onto its only child (Radix `Slot`) instead of rendering a `<div>`. This
     * is how a whole card becomes a link: pass the app's router `Link` as the child, with the card's
     * parts inside it. The card must then hold no other interactive element (no footer buttons);
     * for a card with its own actions, put a stretched link in it instead.
     */
    asChild?: boolean;
  };

/**
 * A surface split into stacked regions — `Card.Header` (optionally holding `Card.Title` and
 * `Card.Description`), `Card.Body`, and `Card.Footer` — with the body optionally split further into
 * `Card.Section`s. Every part is a plain `div`, so style it (color, shadow, layout) via `className`.
 *
 * @example
 * ```tsx
 * <Card variant="floating" divided>
 *   <Card.Header className="bg-hue-yellow-surface">
 *     <Card.Title>Intake Form</Card.Title>
 *     <Card.Description>Submitted · 3 questions</Card.Description>
 *   </Card.Header>
 *   <Card.Body>
 *     <Card.Section>…</Card.Section>
 *     <Card.Section>…</Card.Section>
 *   </Card.Body>
 *   <Card.Footer>…</Card.Footer>
 * </Card>
 * ```
 */
const CardRoot = ({
  className,
  variant = "default",
  padding = "parts",
  divided = false,
  asChild = false,
  ref,
  children,
  ...props
}: CardProps) => {
  const context = useMemo(() => ({ variant, padding, divided }), [variant, padding, divided]);
  const Comp = asChild ? Slot : "div";

  return (
    <CardContext.Provider value={context}>
      <Comp
        ref={ref}
        data-slot="card"
        data-variant={variant}
        className={cn(cardVariants({ variant, padding, divided }), className)}
        {...props}
      >
        {children}
      </Comp>
    </CardContext.Provider>
  );
};

export const Card = Object.assign(CardRoot, {
  Header: CardHeader,
  Title: CardTitle,
  Description: CardDescription,
  Body: CardBody,
  Section: CardSection,
  Footer: CardFooter,
});
